import React, { useContext } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { genThemedStyled, Theme, ThemeContext } from '../../../utilities/theme';
import { ColorCodes, colorsForTheme } from '../../../foundations/color';
import S from '../../../foundations/size';
import { FUTURO_TYPEFACES as F } from '../../../foundations/font';

export type SwitchProps = {
  /** The switch input's name */
  name: string;
  /** Checked state of the switch */
  checked?: boolean;
  /** The switch input's label */
  label?: string;
  disabled?: boolean;
  /** The switch input's hint text */
  hint?: string;
  /** Event when the switch is clicked */
  onClick: (name: string, checked: HTMLInputElement['checked']) => void;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);

  const themeObj = {
    backgroundColor: colors.STEEL,
    checkedBackgroundColor: colors.SWEET_ORANGE,
    knobColor: colors.WHITE,
    checkedKnobColor: colors.SWEET_ORANGE,
    labelColor: colors.SPACE,
    hintColor: colors.STEEL,
  };
  return themeObj;
};

type themeStyles = {
  backgroundColor: ColorCodes;
  checkedBackgroundColor: ColorCodes;
  knobColor: ColorCodes;
  checkedKnobColor: ColorCodes;
  labelColor: ColorCodes;
  hintColor: ColorCodes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

const SwitchWrapper = ThemeStyled.label({
  position: 'relative',
  display: 'inline-block',
  width: '2.25rem',
  height: S.S,
});

const SwitchInput = ThemeStyled.input<{
  tag?: string;
  theme: themeStyles;
}>(({ theme }) => ({
  opacity: 0,
  width: 0,
  height: 0,

  '&:checked': {
    '+ span:before': {
      position: 'absolute',
      content: '""',
      height: '100%',
      width: '100%',
      borderRadius: S.XL,
      backgroundColor: theme.checkedBackgroundColor,
      opacity: 0.4,
    },

    '+ span:after': {
      backgroundColor: theme.checkedKnobColor,
      WebkitTransform: 'translateX(1.375rem)',
      msTransform: 'translateX(1.375rem)',
      transform: 'translateX(1.375rem)',
    },
  },
}));

const SwitchKnob = ThemeStyled.span<{
  theme: themeStyles;
}>(({ theme }) => ({
  position: 'absolute',
  cursor: 'pointer',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  transition: '0.4s',
  borderRadius: S.XL,

  '&:before': {
    position: 'absolute',
    content: '""',
    height: '100%',
    width: '100%',
    borderRadius: S.XL,
    backgroundColor: theme.backgroundColor,
  },

  '&:after': {
    position: 'absolute',
    content: '""',
    height: '1.375rem',
    width: '1.375rem',
    right: '50%',
    bottom: '-0.188rem',
    transition: '0.4s',
    borderRadius: S.XL,
    backgroundColor: theme.knobColor,
    boxShadow:
      '0 0.188rem 0.5rem rgba(0, 0, 0, 0.15), 0 0.063rem 0.063rem rgba(0, 0, 0, 0.16), 0 0.188rem 0.063rem rgba(0, 0, 0, 0.1)',
  },
}));

const SwitchRowWrapper = ThemeStyled.div({
  display: 'flex',
  justifyContent: 'space-between',
});

const TextWrapper = ThemeStyled.div({
  gap: S.XXXS,
  paddingBottom: S.XXXS,
  display: 'flex',
  flexDirection: 'column',
});

const LabelWrapper = ThemeStyled.label<{
  theme: themeStyles;
}>(
  ({ theme }) => ({
    color: theme.labelColor,
  }),
  F.LABEL_SMALL_BOLD
);

const HintWrapper = ThemeStyled.label<{
  theme: themeStyles;
}>(
  ({ theme }) => ({
    color: theme.hintColor,
  }),
  F.LABEL_SMALL
);

/**
 * A futuro switch
 *
 * @argument name string - optional prop. the switch input's name
 * @argument checked boolean - optional prop. checked state of the switch
 * @argument disabled boolean - optional prop. disabled state of the switch
 * @argument label string - optional prop. switch label
 * @argument hint string - optional prop. hint text for switch
 * @argument onClick - required prop. event when the switch is clicked
 *
 * Based off the designs in the figma file:
 *
 * https://www.figma.com/file/xLo63RkYnZPLkkqk3B6yNP/%F0%9F%A4%9602-OM-Android-Library?node-id=280%3A3181
 *
 * @example
 *
 * <Switch
 *  name='Switch'
 *  checked={true}
 *  label='Enabled'
 *  hint='You can disable the product to hide it from your catalogue'
 * />
 */

export const Switch = (props: SwitchProps) => {
  const { name, checked = false, disabled, label, hint, onClick } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onClick(name, event.target.checked);
  };

  return (
    <ThemeProvider theme={getTheme(useContext(ThemeContext))}>
      <SwitchRowWrapper>
        {label && (
          <TextWrapper>
            <LabelWrapper>{label}</LabelWrapper>
            {hint && <HintWrapper>{hint}</HintWrapper>}
          </TextWrapper>
        )}
        <SwitchWrapper>
          <SwitchInput
            type="checkbox"
            aria-label={name}
            disabled={disabled}
            key={checked.toString()}
            name={name}
            defaultChecked={checked}
            onChange={handleChange}
          />
          <SwitchKnob />
        </SwitchWrapper>
      </SwitchRowWrapper>
    </ThemeProvider>
  );
};

export default Switch;
